<template>
    <div>
        <v-dialog
            v-model="projectDataDialog"
            fullscreen
            transition="scale-transition"
            origin="center center">
            <v-card class="d-flex align-start" >
                <div style="width: 100%">
                    <ProjectDetails
                        :user_id="this_userId"
                        :project_id="this_projectId"
                        :project_statuses="project_statuses"
                        :client_info="client_info"
                        :projectDataDialog="projectDataDialog"
                        openTab="overview"
                        @refreshUserProjects="refreshUserProjects" />
                </div>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="buyerDataDialog"
            fullscreen
            transition="scale-transition"
            origin="center center">
            <v-card class="d-flex align-start" >
                <div style="width: 100%">
                    <BuyerDetails
                        :user_id="this_userId"
                        :project_id="this_projectId"
                        :project_statuses="project_statuses"
                        openTab="overview"
                        :currentInterestListings="currentInterestListings"
                        :archivedInterestListings="archivedInterestListings"
                        :available_listings="available_listings"
                        @refreshListings="getListingsForBuyer"
                        @closeBuyerDialog="closeBuyerDialog" />
                </div>
            </v-card>
        </v-dialog>

        <v-card
            v-for="prj of buyerProject" 
            :key="prj.project_id"
            class="mx-9 mt-4 mb-12"
            style="background-color: #fcfcfc">

            <v-hover>
				<template v-slot:default="{ hover }">
                    <v-card-title class="sansation py-0" :class="`elevation-${hover ? 12 : 0}`">
                        <div @click="openBuyerDetails(prj)" class="d-block flex-grow-1 pb-1" style="cursor: pointer">
                            <div class="d-flex d-block align-stretch justify-space-between">
                                <div class="d-inline d-flex align-center pt-2 pb-1">
                                    <customIcon :name="prj.modinfo.module_key" class="d-inline mt-n1 mr-1 info--text text--lighten-3 icon-size-small" />
                                    <div class="d-inline info--text text--lighten-3 label_medium">{{ prj.modinfo.module_name }}</div>
                                </div>

                                <div class="d-flex d-inline justify-end align-center pt-2 pb-1">
                                    <div class="grey--text d-flex d-inline ml-2 align-center">
                                        <div class="d-inline">{{ currentInterestListings.length }} active <div class="d-inline mx-2">&amp;</div> {{ archivedInterestListings.length }} archived interests</div>
                                        <v-divider vertical class="ml-4 mr-2 general_lightgrey--text"></v-divider>
                                    </div>
                                    <div class="primary--text text--darken-1 d-inline ml-2">
                                        {{ prj.status == 'completed' ? 'Profile Complete' : 'Profile Not Complete' }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-card-title>
                </template>
		    </v-hover>
        </v-card>
        
        <div class="my-8">&nbsp;</div>
        
        <div v-if="otherProjects.length > 0" class="d-flex justify-end my-2 mr-9">
            <div class="d-inline font-weight-thin grey--text">{{ otherProjects.length }} {{ project_type }} projects</div>
            <div class="d-inline ml-3">
                <v-icon
                    class="grey--text ml-3"
                    style="cursor: pointer"
                    @click="toggleAllGroups">
                    {{ allGroupsExpanded() ? 'mdi-eye-off' : 'mdi-eye' }}
                </v-icon>
            </div>
        </div>
        
        <v-alert
            v-if="projects.length == 0"
            class="ma-12 sansation_subtitle"
            type="info"
            prominent
            border="left"
            elevation="3">
            There are no {{ project_type }} projects to show at this time.
        </v-alert>

        <div
            v-else
            class="mx-9"> 
            <div
                v-for="group in groupedProjects"
                :key="group.key"
                class="mb-12">
                <div
                    class="d-flex justify-space-between align-center mb-3"
                    style="cursor: pointer"
                    @click="toggleGroupExpansion(group.key)">
                    <div class="font-weight-bold info--text text--lighten-3">
                        <span :class="group.status_text_color">{{ group.label }}</span>
                    </div>
                    <div class="d-flex justify-end align-center">
                        <div class="caption grey--text">{{ group.projects.length }} {{ group.projects.length === 1 ? 'project' : 'projects' }}</div>
                        <v-icon class="ml-3 grey--text">{{ isGroupExpanded(group.key) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                    </div>
                </div>

                <template v-if="isGroupExpanded(group.key)">
                <v-card
                    v-for="prj in group.projects"
                    :key="prj.project_id"
                    class="mb-6"
                    style="background-color: #fcfcfc"
                    tile>
                    <v-hover>
                        <template v-slot:default="{ hover }">
                            <div class="sansation pa-0" :class="`mb-0 elevation-${hover ? 9 : 0}`">
                                <div @click.stop="openProjectDetails( prj )" class="d-block flex-grow-1" style="cursor: pointer;">
                                    <v-progress-linear
                                        :color="`${prj.status_progress_color}`"
                                        height="5"
                                        :value="displayProjectCompletion(prj) == 0 ? 2 : displayProjectCompletion(prj)">
                                    </v-progress-linear>

                                    <div class="d-block d-flex justify-space-between align-center pt-1 pb-1 px-4">
                                        <div class="d-inline d-flex align-center">
                                            <customIcon :name="prj.modinfo.module_key" class="d-inline ml-n2 mr-1 info--text text--lighten-3 icon-size-small" />
                                            <div class="d-inline info--text text--lighten-3 label_medium">
                                                {{ prj.modinfo.module_name }}
                                            </div>
                                            <div v-if="prj.is_annual" class="d-inline error--text text--darken-3">
                                                <div class="mx-2 d-inline">::</div><div class="d-inline">Annual Project</div>
                                            </div>
                                            <div v-if="prj.hide_for_user" class="d-inline ml-3">
                                                <v-tooltip top
                                                    :open-delay="500"
                                                    :close-delay="200">
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-icon
                                                            size="20"
                                                            color="info lighten-3"
                                                            v-bind="attrs"
                                                            v-on="on">
                                                            mdi-eye-off
                                                        </v-icon>
                                                    </template>
                                                    <span>This project is hidden from the user's dashboard</span>
                                                </v-tooltip>
                                            </div>
                                        </div>

                                        <div class="d-inline d-flex justify-end align-center">
                                            <div class="d-inline mr-3 info--text text--lighten-3">Status:</div>
                                            <div :class="`d-inline ` + prj.status_text_color">
                                                {{ prj.status_internal_label }}
                                            </div>
                                            <div class="d-inline mx-2" :class="prj.status_text_color">::</div>
                                            <div class="d-inline">
                                                <div :class="prj.status_text_color">
                                                    {{ displayProjectCompletion(prj) }}%
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="d-block d-flex justify-space-between align-center pb-1 px-4">
                                        <div class="d-inline d-flex">
                                            <div v-if="prj.project_title" class="font-weight-thin grey--text">{{ prj.project_title }}</div>
                                            <div v-if="prj.project_title && prj.internal_project_title" class="font-weight-thin grey--text text--darken-2">
                                                <div class="mx-2 d-inline">::</div><div class="d-inline">{{ prj.internal_project_title }}</div>
                                            </div>
                                            <div v-if="!prj.project_title && prj.internal_project_title" class="font-weight-thin grey--text text--darken-2">{{ prj.internal_project_title }}</div>
                                        </div>

                                        <div class="d-inline d-flex justify-end">
                                            <div class="d-inline mr-3 info--text text--lighten-3">Current Stage:</div>
                                            <div class="d-inline primary--text">{{ prj.stage_name_text }}</div>
                                        </div>
                                    </div>

                                    <div class="d-block d-flex justify-space-between align-center pt-1 pb-3 px-4">
                                        <div class="d-inline d-flex">
                                            <div class="grey--text font-weight-thin">
                                                {{ prj.dlvs.length ? `Latest deliverable uploaded: ${formatThisDate(latestDeliverable(prj.dlvs, 'file_upload_date'))}` : '' }}
                                                {{ !prj.dlvs.length ? `Deliverables have not been uploaded yet` : '' }}
                                            </div>
                                        </div>

                                        <div class="d-inline d-flex justify-end">
                                            <div class="d-inline error--text text--darken-3 mr-1" v-if="prj.expedite && !prj.is_annual">
                                                <div class="d-inline">Priority Access</div><div class="mx-2 d-inline">::</div>
                                            </div>
                                            <div class="info--text text--lighten-3 d-inline">{{ prj.status == 'complete' ? 'Completion Date:' : 'Est. Completion Date:' }}</div>
                                            <div class="ml-3 primary--text d-inline">{{ prj.close_date == '' ? 'N/A' : formatThisDate(prj.close_date) }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </v-hover>
                </v-card>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
    import axios from "axios";
    import Cookie from 'js-cookie';
    import appUrls from '../helpers/environment'
    import { findLatestDatedRecord } from '../helpers/utilities'
    import { dataTimeDisplayFormatter } from './utilities'

    // import dateFormat, { masks } from "dateformat"

    import ProjectDetails from './ProjectDetails.vue'
    import BuyerDetails from './BuyerDetails.vue'

    export default {
        name: 'ProjectOverview',
    
        components: {
            ProjectDetails,
            BuyerDetails
        },
    
        props: {
            userId: String,
            projects: Array,
            client_info: Array,
            project_type: String,
            fetchProjectsForType: Function,
            userFeedback: Function
        },

        data () {
            return {
                projectDataDialog: false,
                buyerDataDialog: false,
                buyerProject: [],
                current_listings: [],
                available_listings: [],
                otherProjects: [],
                groupedProjects: [],
                expandedGroups: {},
                this_userId: '',
                this_projectId: '',
                project_statuses: [],
                statusGroupDefinitions: [
                    { key: 'in_progress', statuses: ['processing', 'in_progress'], fallbackLabel: 'In Progress' },
                    { key: 'in_review', statuses: ['review', 'resubmitted'], fallbackLabel: 'In Review' },
                    { key: 'awaiting_questionnaire', statuses: ['open', 'awaiting_questionnaire'], fallbackLabel: 'Awaiting Questionnaire' },
                    { key: 'waiting_on_client', statuses: ['awaiting_data', 'awaiting_client_decision'], fallbackLabel: 'Waiting on Client' },
                    { key: 'on_pause', statuses: ['another_project', 'specified_date', 'personal_life'], fallbackLabel: 'On Pause' },
                    { key: 'on_hold', statuses: ['client_unresponsive', 'other'], fallbackLabel: 'On Hold' },
                    { key: 'complete', statuses: ['complete', 'completed'], fallbackLabel: 'Complete' },
                    { key: 'incomplete', statuses: ['closed', 'upgrade', 'downgrade', 'refunded', 'cloned'], fallbackLabel: 'Incomplete/Closed' }
                ],
            }
        },

        computed: {
            currentInterestListings () {
                return this.current_listings.filter( listing => !listing.is_archived )
            },

            archivedInterestListings () {
                return this.current_listings.filter( listing => listing.is_archived )
            }
        },

        methods: {
            openProjectDetails: async function ( prj ) {
                this.projectDataDialog = true
                this.this_userId = prj.user_id
                this.this_projectId = prj.project_id
            },

            closeDetailDialog: function ( archiveWork ) {
                this.refreshUserProjects( true )
                this.projectDataDialog = false
                this.this_userId = ''
                this.this_projectId = ''
            },

            openBuyerDetails: async function ( prj ) {
                this.buyerDataDialog = true
                this.this_userId = prj.user_id
                this.this_projectId = prj.project_id
            },

            closeBuyerDialog: function ( archiveWork ) {
                this.refreshUserProjects( true )
                this.buyerDataDialog = false
                this.this_userId = ''
                this.this_projectId = ''
            },

            getListingsForBuyer: async function (user_id) {
                axios.get(`${appUrls.api}/api/admin/available_listings/${user_id}`, {
                    headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                })
                .then( res => {
                    if(res.data.outcome) {
                        this.current_listings = res.data.current_listings || []
                        this.available_listings = res.data.available_listings || []
                    }
                })
                .catch(function (error) {
                    console.log(error);
                });
            },

            refreshUserProjects: function ( val ) {
                this.$emit( 'fetchProjectsForType', val )
            },

            isGroupExpanded: function ( groupKey ) {
                return this.expandedGroups[groupKey] !== false
            },

            toggleGroupExpansion: function ( groupKey ) {
                this.$set(this.expandedGroups, groupKey, !this.isGroupExpanded(groupKey))
            },

            allGroupsExpanded: function () {
                const groupKeys = this.groupedProjects.map(group => group.key)
                return groupKeys.length > 0 && groupKeys.every(groupKey => this.isGroupExpanded(groupKey))
            },

            toggleAllGroups: function () {
                const shouldCollapse = this.allGroupsExpanded()
                this.groupedProjects.forEach(group => {
                    this.$set(this.expandedGroups, group.key, !shouldCollapse)
                })
            },

            // formatDate: function ( date, mask="mm/dd/yyyy" ) {
            //     return dateFormat(date.substr(0,10)+' 00:00:00', mask)
            // },
            formatThisDate: function ( date ) {
                if (!date) return '';
                
                const options = {
                    month: '2-digit', 
                    day: '2-digit', 
                    year: 'numeric'
                }
                return dataTimeDisplayFormatter(date, options)
                // return dateFormat(date.substr(0,10)+' 00:00:00', mask)
            },

            latestDeliverable: function ( date, field ) {
                return findLatestDatedRecord(date, field)[field]
            },

            displayProjectCompletion: function ( prj ) {
                if (prj && Array.isArray(prj.questionnaire_sections) && prj.questionnaire_sections.length) {
                    const completedSections = prj.questionnaire_sections.filter(section => section.is_complete).length
                    return Math.ceil((completedSections / prj.questionnaire_sections.length) * 100)
                }

                return prj && prj.complete ? Math.ceil(prj.complete) : 0
            },

            getStatusData: async function () {
                try {
                    const get_project_status_data = await axios.get(`${appUrls.api}/api/project_status_data`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" }
                    })

                    this.project_statuses = get_project_status_data.data.status_data
                    this.project_statuses.forEach(obj => {
                        obj.count = 0;
                    });
                    this.groupOtherProjectsByStatus()

                } catch ( error ) {
                    console.log(error);
                }
            },

            groupOtherProjectsByStatus: function () {
                const groupedProjects = this.statusGroupDefinitions.map(group => {
                    const projects = this.otherProjects
                        .filter(prj => group.statuses.includes(prj.status))
                        .sort((a, b) => {
                            const parseDateValue = (dateValue) => {
                                if (dateValue === null || dateValue === undefined || dateValue === '') return null

                                if (dateValue instanceof Date) {
                                    const time = dateValue.getTime()
                                    return Number.isNaN(time) ? null : time
                                }

                                if (typeof dateValue === 'number') {
                                    if (!Number.isFinite(dateValue)) return null
                                    return dateValue < 100000000000 ? dateValue * 1000 : dateValue
                                }

                                if (typeof dateValue !== 'string') return null

                                const trimmed = dateValue.trim()
                                if (!trimmed || trimmed.toLowerCase() === 'n/a') return null

                                const parsedDirect = Date.parse(trimmed)
                                if (!Number.isNaN(parsedDirect)) return parsedDirect

                                const isoLike = trimmed.match(/^\d{4}-\d{2}-\d{2}(\s\d{2}:\d{2}:\d{2}(\.\d+)?)?$/)
                                if (isoLike) {
                                    const normalized = trimmed.includes(' ')
                                        ? `${trimmed.replace(' ', 'T')}Z`
                                        : `${trimmed}T00:00:00Z`
                                    const parsedNormalized = Date.parse(normalized)
                                    if (!Number.isNaN(parsedNormalized)) return parsedNormalized
                                }

                                return null
                            }

                            const getTimeValue = (...dateCandidates) => {
                                for (const candidate of dateCandidates) {
                                    const parsed = parseDateValue(candidate)
                                    if (parsed !== null) return parsed
                                }
                                return Number.POSITIVE_INFINITY
                            }

                            const aCloseDateTime = getTimeValue(a.close_date)
                            const bCloseDateTime = getTimeValue(b.close_date)
                            const aHasCloseDate = Number.isFinite(aCloseDateTime)
                            const bHasCloseDate = Number.isFinite(bCloseDateTime)

                            if (aHasCloseDate && bHasCloseDate) {
                                return aCloseDateTime - bCloseDateTime
                            }

                            if (aHasCloseDate && !bHasCloseDate) {
                                return -1
                            }

                            if (!aHasCloseDate && bHasCloseDate) {
                                return 1
                            }

                            const aInitiatedTime = getTimeValue(a.date_initialized, a.date_initiated, a.date_initialized_label, a.created_date)
                            const bInitiatedTime = getTimeValue(b.date_initialized, b.date_initiated, b.date_initialized_label, b.created_date)

                            return aInitiatedTime - bInitiatedTime
                        })
                    const matchedStatus = group.statuses
                        .map(statusValue => this.project_statuses.find(status => status.value === statusValue))
                        .find(Boolean)

                    return {
                        key: group.key,
                        label: matchedStatus && matchedStatus.external_label ? matchedStatus.external_label : group.fallbackLabel,
                        status_text_color: matchedStatus && matchedStatus.status_text_color ? matchedStatus.status_text_color : '',
                        projects
                    }
                }).filter(group => group.projects.length > 0)

                const expandedGroups = {}
                groupedProjects.forEach(group => {
                    expandedGroups[group.key] = this.expandedGroups[group.key] !== undefined
                        ? this.expandedGroups[group.key]
                        : true
                })

                this.expandedGroups = expandedGroups
                this.groupedProjects = groupedProjects
            },

            separateBuyerProfile: function () {
                this.buyerProject = this.projects.filter(prj => { return prj.modinfo.module_key == 'buyer' })
                this.otherProjects = this.projects.filter(prj => { return prj.modinfo.module_key != 'buyer' })
                this.groupOtherProjectsByStatus()

                if (this.buyerProject.length) this.getListingsForBuyer(this.buyerProject[0].user_id)
            }
        },

        watch: {
            projects: {
                handler: function () {
                    this.separateBuyerProfile()
                },
                deep: true,
                immediate: true
            }
        },

        mounted () {
            this.getStatusData()
        }
    }
</script>

<style scoped>
    .bg_pewter {
        background-color: #E9EAEC !important;
    }
    .menu_bg_primary {
        background-color: #eef7f8 !important;
    }
    .icon-size {
        width: 3rem;
    }
    .icon-size-small {
        min-width: 2.25rem;
        max-width: 2.25rem;
    }
    .icon-size-header {
        min-width: 2.25rem;
        max-width: 2.25rem;
    }
    .icon-center {
        margin: 0 auto;
    }
    .refresh_response {
        font-size: .8rem;
    }
    .click_indicator {
		width: 50px; 
		height: 3px; 
		border-top: 1px dotted rgba(0,0,0,.3);
		border-bottom: 1px dotted rgba(0,0,0,.3);
	}
    a.buyers_link:hover {
        text-decoration: underline;
    }
</style>


// WEBPACK FOOTER //
// src/components/ProjectOverview.vue